import { MaybeRef, Ref, unref } from 'vue'
import { storeToRefs } from 'pinia'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { until } from '@vueuse/core'
import { useAutopilotStore } from '../stores/autopilot'
import { usePersonalStore } from '../stores/personal'
import { useAnalyzeStore } from '../stores/analyze'
import { useUserActivityStore } from '../stores/userActivity'
import { useTargetingSuggestionStore } from '../stores/targetingSuggestion'
import { useSavedAudiencesStore } from '../stores/savedAudiences'
import { isError, MaybeError } from './constants'
import {
  api,
  AudienceConfig,
  AudienceConfigDetail,
  DeepThoughtXStatsTrend,
  GetTrendingTopicsQuery,
  Insights,
  PRO_TARGETING_AUTH_TOKEN,
  RequestError,
  schema,
  StandaloneAudienceConfig,
  StandaloneAudienceConfigDetail,
  StandAloneComputeAgeGroupsInput,
  StandAloneExploreDemographicsInput,
  StandAloneExploreEnrichmentsInput,
  StandaloneExploreExtractDataHandlesInput,
  StandaloneExploreExtractDataKeywordsInput,
  StandaloneExploreExtractDataTweetKeywordsInput,
  StandaloneExploreExtractPostDataHandlesInput,
  StandAloneExploreFollowerGrowthInput,
  StandAloneExplorePostsDataInput,
  StandaloneExploreQueryAnalyzerInput,
  StandaloneExploreSearchInput,
  StandAloneExploreSentimentAnalysisInput,
  StandAloneExploreTopAccountsInput,
  StandAloneExploreTopFollowersInput,
  StandAloneExploreTopPostsInput,
  StandaloneFindRelatedAudiencesInput,
  StandAloneGenerateShareOfVoiceKeywordsInput,
  StandaloneHandle,
  StandaloneInsightsAnalyzerGenerateSheetInput,
  StandaloneNativePackGeneratorInput,
  StandAlonePostActivityInput,
  StandalonePostMetadataHandleInput,
  StandaloneUserActivityEntryInput,
  StandaloneXConversation,
  StandaloneXConversationInput,
  StandaloneXploreDetailInput,
  StandAloneXplorePostsSentimentAnalysisInput,
} from '@/api'
import { useChromeExtension } from '@/extension'
import { messageService } from '@/app'

dayjs.extend(utc)

const { adAccountId } = useChromeExtension()

export type ChainRequestItem<TInput extends (...args: any[]) => any> = {
  fn: TInput | ((...args: Parameters<TInput>) => Promise<ReturnType<TInput>>)
  input: MaybeRef<Parameters<TInput>[0]>
  variable: Ref<any>
  loading?: Ref<boolean>
  tookTooLong?: Ref<boolean>
  tookTooLongTimeout?: number
}

export function isStandaloneConfig(
  config: AudienceConfig | AudienceConfigDetail | StandaloneAudienceConfig | StandaloneAudienceConfigDetail,
): config is StandaloneAudienceConfig {
  return 'child_count' in config
}

export function isStandaloneConfigDetail(
  config: AudienceConfig | AudienceConfigDetail | StandaloneAudienceConfig | StandaloneAudienceConfigDetail,
): config is StandaloneAudienceConfigDetail {
  return 'child' in config || 'parent' in config
}

export function useTargetingListBuilder() {
  const userActivityStore = useUserActivityStore()
  const { audienceCreatorQueryId } = storeToRefs(userActivityStore)
  const analyzeStore = useAnalyzeStore()
  const {
    loadingConversation,
    runningQuery,
    loadingEnrichments,
    loadingRelevantConfigs,
    loadingPostMetadata,
    submittedQueryType,
    submittedHandle,
    similarHandles,
    error,
  } = storeToRefs(analyzeStore)
  const targetingSuggestion = useTargetingSuggestionStore()
  const autopilotStore = useAutopilotStore()
  const { processingTargetingList, insights, loading } = storeToRefs(autopilotStore)
  const personalStore = usePersonalStore()
  const { email, signedInAs, locale } = storeToRefs(personalStore)
  const savedAudiencesStore = useSavedAudiencesStore()
  const {
    email: savedAudienceEmail,
    queryType: savedAudienceQueryType,
    audiences: savedAudiences,
    loading: loadingUserAudiences,
  } = storeToRefs(savedAudiencesStore)

  async function fetchTargetingList(pk: number) {
    let response: Insights | undefined

    async function fetchInsights() {
      response = await api.call(
        schema.StandaloneInsights.retrieve,
        { path: { pk } },
        {
          headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` },
        },
      )

      finishedProcessing()
    }

    let promiseResolver: (v: any) => void
    const promise = new Promise((resolve) => {
      promiseResolver = resolve
    })

    function finishedProcessing() {
      response?.targeting_package?.hashtags?.length || response?.targeting_package?.keywords?.length
        ? promiseResolver(response)
        : setTimeout(fetchInsights, 3000)
    }

    fetchInsights()

    await promise
    processingTargetingList.value = false
    insights.value = response
    return response
  }

  async function generateDescriptionSummary(description: string, email?: string) {
    try {
      loading.value = true
      const response = await api.call(
        schema.StandaloneDescriptionAnalyzer.create,
        {
          body: {
            description,
            email,
          },
        },
        { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
      )

      return response
    } finally {
      loading.value = false
    }
  }

  async function generateCampaignAttributes(targetingCriteria: StandaloneNativePackGeneratorInput, email?: string) {
    try {
      loading.value = true
      processingTargetingList.value = true
      const response = await api.call(
        schema.StandaloneNativePackGenerator.create,
        {
          body: { ...targetingCriteria, email, ad_account_id: adAccountId.value },
        },
        { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
      )
      if (
        isError<
          | (typeof schema.StandaloneNativePackGenerator.create)['responseType']
          | (typeof schema.StandaloneNativePackGenerator.create)['errors']
        >(response)
      )
        throw new Error(response.detail)

      fetchTargetingList(response.id)

      return response
    } catch (e) {
      if (
        e instanceof Object &&
        'data' in e &&
        e.data instanceof Object &&
        'email' in e.data &&
        (e.data.email as string).includes('Invalid access')
      ) {
        throw new Error('Invalid email address')
      }

      if (e instanceof RequestError && e.message.includes('No audience found')) {
        throw new Error('No audience found')
      }

      throw new Error('Failed to generate campaign attributes')
    } finally {
      loading.value = false
    }
  }

  async function getSimilarHandles(): Promise<StandaloneHandle[]> {
    if (typeof submittedHandle.value !== 'string' || !submittedHandle.value) {
      return []
    }

    const response = await api.call(
      schema.StandaloneAudienceRequest.similar_handles,
      {
        query: { handle: submittedHandle.value },
      },
      { authHeaderToken: 'pro_targeting' },
    )
    similarHandles.value = response.handles

    return response.handles
  }

  async function analyzeQuery(body: StandaloneExploreQueryAnalyzerInput) {
    const response = await api.call(
      schema.StandaloneExploreQueryAnalyzer.create,
      { body: { ...body, ad_account_id: adAccountId.value, email: email.value } },
      { authHeaderToken: 'token' },
    )

    audienceCreatorQueryId.value = response.audience_creator_query_id

    return response
  }

  async function analyzeHandles(body: StandaloneExploreExtractDataHandlesInput) {
    try {
      const response = await api.call(
        schema.StandaloneExploreExtractDataHandles.create,
        { body },
        { authHeaderToken: 'token' },
      )

      return response
    } catch (err: any) {
      if (isError(err) && (err.detail.includes('No data found') || err.detail.includes('No audience found'))) {
        error.value =
          "The search you did doesn't provide enough results. Please broaden it by using a more general topic."
        getSimilarHandles()

        return err
      }

      if (isError(err) && err.detail.includes("couldn't process the inputted handle")) {
        error.value =
          "The handle you entered cannot be processed. It may not exist or it may be that the user is 'protected' on X."
        getSimilarHandles()

        return err
      }

      throw err
    }
  }

  async function analyzeFollowerGrowth(body: StandAloneExploreFollowerGrowthInput) {
    const response = await api.call(
      schema.StandAloneExploreFollowerGrowth.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function analyzeTopFollowers(body: StandAloneExploreTopFollowersInput) {
    const response = await api.call(schema.StandAloneExploreTopFollowers.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function analyzePostDataHandles(body: StandaloneExploreExtractPostDataHandlesInput) {
    const response = await api.call(
      schema.StandaloneExploreExtractPostDataHandles.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function analyzeKeywords(body: StandaloneExploreExtractDataKeywordsInput) {
    const response = await api.call(
      schema.StandaloneExploreExtractDataKeywords.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function analyzeTopAccounts(body: StandAloneExploreTopAccountsInput) {
    const response = await api.call(schema.StandAloneExploreTopAccounts.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function analyzeDemographics(body: StandAloneExploreDemographicsInput) {
    const response = await api.call(schema.StandAloneExploreDemographics.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function analyzeEnrichments(body: StandAloneExploreEnrichmentsInput) {
    try {
      loadingEnrichments.value = true
      const response = await api.call(
        schema.StandAloneExploreEnrichments.create,
        { body },
        { authHeaderToken: 'token' },
      )

      return response
    } finally {
      loadingEnrichments.value = false
    }
  }

  async function analyzePostsData(body: StandAloneExplorePostsDataInput) {
    const response = await api.call(schema.StandAloneExplorePostsData.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function analyzeTopPosts(body: StandAloneExploreTopPostsInput) {
    const response = await api.call(schema.StandAloneExploreTopPosts.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function voicesInConversation(body: StandAloneGenerateShareOfVoiceKeywordsInput) {
    const response = await api.call(
      schema.StandAloneGenerateShareOfVoiceKeywords.create,
      { body: { ...body, locale: locale.value } },
      { authHeaderToken: 'token' },
    )

    return response
  }

  function transformVoice(keywords: string[][], suffixes: string[]) {
    const result = []
    for (const group of keywords) {
      for (const suffix of suffixes) {
        result.push([...group, suffix])
      }
    }
    return result
  }

  function keywordsToQuery(keywords: string[][], exclude: string[]) {
    const excludeKeywords = exclude.map((e) => ` NOT ${e}`).join('')
    return (
      keywords.map((group) => (group.length > 1 ? `(${group.join(' AND ')})` : group[0])).join(' OR ') + excludeKeywords
    )
  }

  function queryToKeywords(query: string): any {
    if (!query) return []
    const mainQuery = query.split(/\s+NOT\s+/i)[0]
    return [
      mainQuery
        .split(/,\s*|\s+OR\s+/gi)
        .filter((g) => g.trim())
        .map((group) =>
          group.trim().startsWith('(') && group.trim().endsWith(')')
            ? group
                .trim()
                .split(/\s+AND\s+/i)
                .map((s) => s.replace(/\(/g, '').replace(/\)/g, '').trim())
            : group.split(/\s+AND\s+/i).map((s) => s.replace(/\(/g, '').replace(/\)/g, '').trim()),
        ),
      query.split(/\sNOT\s/i).slice(1),
    ]
  }

  async function postActivity(body: StandAlonePostActivityInput) {
    const response = await api.call(schema.StandAlonePostActivity.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  async function trendingTopics(query: GetTrendingTopicsQuery) {
    const response = await api.call(schema.StandaloneXStats.trending_topics, { query }, { authHeaderToken: 'token' })

    if (Array.isArray(response)) {
      return response as DeepThoughtXStatsTrend[]
    }

    throw new Error('Invalid response format for trending topics')
  }

  async function analyzeConversationAudience(body: StandaloneExploreExtractDataTweetKeywordsInput) {
    const response = await api.call(
      schema.StandaloneExploreExtractDataTweetKeywords.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }
  
  async function analyzeSentiments(body: StandAloneExploreSentimentAnalysisInput) {
    const response = await api.call(
      schema.StandAloneExploreSentimentAnalysis.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function analyzePostMetadataHandle(body: StandalonePostMetadataHandleInput) {
    try {
      loadingPostMetadata.value = true
      const response = await api.call(
        schema.StandalonePostMetadataHandle.create,
        { body },
        { authHeaderToken: 'token' },
      )

      return response
    } finally {
      loadingPostMetadata.value = false
    }
  }

  async function getRelevantConfigs(body: StandaloneFindRelatedAudiencesInput) {
    try {
      loadingRelevantConfigs.value = true
      const response = await api.call(
        schema.StandaloneFindRelatedAudiences.create,
        { body },
        { authHeaderToken: 'token' },
      )
      if (isError(response)) throw new Error(response.detail)

      targetingSuggestion.request(response.audience_configs.length)

      return response
    } finally {
      loadingRelevantConfigs.value = false
    }
  }

  async function analyzeConversation(
    body: StandaloneXConversationInput,
  ): Promise<MaybeError<StandaloneXConversation> | undefined> {
    try {
      loading.value = true
      loadingConversation.value = true
      const response = await api.call(
        schema.StandaloneXConversation.create,
        {
          body: { ...body, ad_account_id: adAccountId.value, email: email.value },
        },
        {
          authHeaderToken: 'token',
        },
      )

      return response
    } catch (e) {
      if (e instanceof RequestError && e.message.includes('The request took too long to process')) {
        return {
          detail: e.message,
        }
      }

      throw e
    } finally {
      loading.value = false
      loadingConversation.value = false
    }
  }

  async function analyzeAge(body: StandAloneComputeAgeGroupsInput) {
    const response = await api.call(
      schema.StandAloneComputeAgeGroups.create,
      {
        body,
      },
      {
        authHeaderToken: 'token',
      },
    )
    if (isError(response)) throw new Error(response.detail)

    return response.age_percentages
  }

  async function analyzePost(body: StandAloneXplorePostsSentimentAnalysisInput) {
    const response = await api.call(
      schema.StandAloneXplorePostsSentimentAnalysis.create,
      { body },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function voiceAliases(voice: string) {
    const response = await api.call(
      schema.StandAloneGenerateShareOfVoiceAliases.create,
      { body: { keyword: voice, locale: locale.value } },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function downloadSheet(body: StandaloneInsightsAnalyzerGenerateSheetInput) {
    try {
      loading.value = true
      const response = await api.call(
        schema.StandaloneInsightsAnalyzer.create_sheet,
        { body },
        { authHeaderToken: 'token' },
      )

      return response
    } finally {
      loading.value = false
    }
  }

  async function getUserAudiences() {
    try {
      loadingUserAudiences.value = true
      if (!savedAudienceEmail.value) {
        await until(savedAudienceEmail).toBeTruthy()
      }

      const normalisedQueryType = savedAudienceQueryType.value === 'all' ? undefined : savedAudienceQueryType.value
      const query = {
        email: savedAudienceEmail.value,
        query_type: normalisedQueryType,
      }
      const response = await api.call(schema.StandaloneExploreSearch.list, { query }, { authHeaderToken: 'token' })

      savedAudiences.value = response

      return response
    } finally {
      loadingUserAudiences.value = false
    }
  }

  async function deleteUserAudience(pk: number) {
    const response = await api.call(
      schema.StandaloneExploreSearch.destroy,
      { path: { pk } },
      { authHeaderToken: 'token' },
    )

    return response
  }

  async function getInsightsFromConfig(config: AudienceConfig | AudienceConfigDetail | number) {
    const id = typeof config === 'number' ? config : config.id
    const response = await api.call(
      schema.StandaloneConfigGenerateExploreInsights.create,
      {
        body: {
          ad_account_id: adAccountId.value,
          email: signedInAs.value,
          audience_config_id: id,
        },
      },
      { authHeaderToken: 'extension' },
    )

    return response.insights
  }

  async function createUserAudience(
    body: Omit<StandaloneExploreSearchInput, 'audience_creator_query_id' | 'query_type' | 'email'>,
  ) {
    if (!signedInAs.value) {
      messageService.error('Failed to create audience. Please log out and log in again to create a new audience.', {
        timeout: 0,
      })
      throw new Error('Failed to create audience. Please log out and log in again to create a new audience.')
    }

    if (!audienceCreatorQueryId.value) {
      messageService.error('Failed to create audience. Please wait until the audience is ready.', { timeout: 0 })
      throw new Error('Failed to create audience. Please wait until the audience is ready.')
    }

    const query_type =
      submittedQueryType.value === 'handle'
        ? 'handle_analysis'
        : submittedQueryType.value === 'conversation'
          ? 'conversation_analysis'
          : submittedQueryType.value === 'post'
            ? 'post_analysis'
            : 'audience_analysis'

    const response = await api.call(
      schema.StandaloneExploreSearch.create,
      {
        body: {
          ...body,
          email: signedInAs.value,
          audience_creator_query_id: audienceCreatorQueryId.value,
          query_type,
        },
      },
      { authHeaderToken: 'token' },
    )

    messageService.success(
      'Audience created successfully. You may find this audience <a href="/saved-audiences" target="_blank" class="text-blue-600 underline">in your saved audiences</a>.',
      { timeout: 10000, content: 'html' },
    )
    return response
  }

  async function createUserActivityEntry(body: StandaloneUserActivityEntryInput) {
    const response = await api.call(schema.StandaloneUserActivityEntry.create, { body }, { authHeaderToken: 'token' })

    return response
  }

  function isValidFunction(
    n: string,
  ): n is
    | keyof ReturnType<typeof functionsThatCreateAudienceCreatorQuery>
    | keyof ReturnType<typeof functionsThatCreateUserActivityEntry> {
    return Object.keys(functionsThatCreateAudienceCreatorQuery()).includes(n)
  }

  const FRIENDLY_FUNCTION_NAME: Partial<
    Record<
      | keyof ReturnType<typeof functionsThatCreateAudienceCreatorQuery>
      | keyof ReturnType<typeof functionsThatCreateUserActivityEntry>
      | 'downloadZippedElements',
      string
    >
  > = {
    downloadSheet: 'Exported sheet',
    downloadZippedElements: 'Exported images',
  }

  async function withInitialUserActivityEntry<T = any>(fn: (...args: any) => T, ...args: any): Promise<T> {
    const functionName = fn.name

    const response = await fn(...args)

    audienceCreatorQueryId.value =
      response &&
      typeof response === 'object' &&
      'audience_creator_query_id' in response &&
      typeof response.audience_creator_query_id === 'number'
        ? response.audience_creator_query_id
        : undefined

    createUserActivityEntry({
      action_at: dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
      action: isValidFunction(functionName) ? FRIENDLY_FUNCTION_NAME[functionName] : functionName,
      audience_creator_query_id: audienceCreatorQueryId.value,
    })

    return response
  }

  async function getExploreDetail(body: StandaloneXploreDetailInput) {
    const response = await api.call(
      schema.StandAloneXploreDetail.create,
      {
        body,
      },
      {
        authHeaderToken: 'token',
      },
    )

    return response
  }

  async function withInitialUserActivityEntryFromClassifier<T = any>(
    fn: (...args: any) => T,
    ...args: any
  ): Promise<T> {
    const functionName = fn.name

    const response = await fn(...args)

    const classifierId =
      response &&
      typeof response === 'object' &&
      'classifier_id' in response &&
      typeof response.classifier_id === 'number'
        ? response.classifier_id
        : undefined

    createUserActivityEntry({
      action_at: dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
      action: isValidFunction(functionName) ? FRIENDLY_FUNCTION_NAME[functionName] : functionName,
      classifier_id: classifierId,
      context: 'Clicked on category on the explore page',
    })

    return response
  }

  async function withContinuingUserActivityEntry<T = any>(
    fn: (...args: any) => T,
    context: string | undefined = undefined,
    ...args: any
  ): Promise<T> {
    const functionName = fn.name

    createUserActivityEntry({
      action_at: dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
      context,
      action: isValidFunction(functionName) ? FRIENDLY_FUNCTION_NAME[functionName] : functionName,
      audience_creator_query_id: audienceCreatorQueryId.value,
    })

    return await fn(...args)
  }

  function functionsThatCreateUserActivityEntry() {
    return {
      downloadSheet: (...args: Parameters<typeof downloadSheet>) =>
        withContinuingUserActivityEntry(downloadSheet, 'Download sheet: ' + args[0].title, ...args),
    }
  }

  function functionsThatCreateAudienceCreatorQuery() {
    return {
      analyzeQuery: (...args: Parameters<typeof analyzeQuery>) => withInitialUserActivityEntry(analyzeQuery, ...args),
      analyzePost: (...args: Parameters<typeof analyzePost>) => withInitialUserActivityEntry(analyzePost, ...args),
      analyzeConversation: (...args: Parameters<typeof analyzeConversation>) =>
        withInitialUserActivityEntry(analyzeConversation, ...args),
      analyzeConversationForCompetitor: (...args: Parameters<typeof analyzeConversation>) =>
        withInitialUserActivityEntry(analyzeConversation, ...args),
    }
  }

  async function promisedRequest<T = any, TT = any>(
    fn: (input: T) => Promise<MaybeError<Partial<TT>>>,
    input: MaybeRef<T>,
    dataRef: Ref<TT>,
    loading?: Ref<boolean>,
    tookTooLong?: Ref<boolean>,
    tookTooLongTimeout?: number,
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        if (loading) {
          loading.value = true
        }

        if (tookTooLong) {
          tookTooLong.value = false
          const snapShotRunningQuery = runningQuery.value
          setTimeout(() => {
            if (loading && loading.value && runningQuery.value === snapShotRunningQuery) {
              tookTooLong.value = true
            }
          }, tookTooLongTimeout ?? 30000)
        }

        input = unref(input)
        const response = await fn(input)

        if (isError(response)) {
          error.value = response.detail
          throw new Error(response.detail)
        }

        if (response && typeof response === 'object') {
          if (Array.isArray(response)) {
            dataRef.value = response as TT
          } else {
            Object.entries(response).forEach(([key, value]) => {
              if (dataRef.value) {
                ;(dataRef.value as any)[key] = value
              }
            })
          }
        }

        resolve(true)
      } catch (e) {
        reject()
      } finally {
        if (loading) {
          loading.value = false
        }
      }
    })
  }

  async function chainRequests(
    requestGroups: Array<
      Array<{
        fn: (input: any) => Promise<any>
        input: any
        variable: any
        loading?: Ref<boolean>
        tookTooLong?: Ref<boolean>
        tookTooLongTimeout?: number
      }>
    >,
  ) {
    for (const group of requestGroups) {
      await Promise.allSettled(
        group.map(({ fn, input, variable, loading, tookTooLong, tookTooLongTimeout }) =>
          promisedRequest(fn, input, variable, loading, tookTooLong, tookTooLongTimeout),
        ),
      )
    }
  }

  return {
    loading,
    postActivity,
    withContinuingUserActivityEntry,
    withInitialUserActivityEntryFromClassifier,
    analyzeKeywords,
    analyzeHandles,
    analyzeFollowerGrowth,
    analyzeTopFollowers,
    analyzePostDataHandles,
    analyzeTopAccounts,
    analyzeDemographics,
    analyzeEnrichments,
    analyzePostsData,
    analyzeTopPosts,
    analyzePostMetadataHandle,
    analyzeConversationAudience,
    analyzeConversationForHandle: (args: any) => analyzeConversation({ ...unref(args), input_type: 'handle_analysis' }),
    ...functionsThatCreateUserActivityEntry(),
    ...functionsThatCreateAudienceCreatorQuery(),
    generateDescriptionSummary,
    generateCampaignAttributes,
    analyzeAge,
    getRelevantConfigs,
    voicesInConversation,
    trendingTopics,
    transformVoice,
    keywordsToQuery,
    queryToKeywords,
    voiceAliases,
    getExploreDetail,
    chainRequests,
    getUserAudiences,
    deleteUserAudience,
    createUserAudience,
    getInsightsFromConfig,
    analyzeSentiments,
  }
}

export function useTargetingLocations() {
  async function searchTargetingLocations(country: string, search: string) {
    const response = await api.call(
      schema.StandaloneTargetingLocations.search,
      { body: { country_code: country, keywords: search } },
      { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
    )

    return response
  }

  async function getTargetingLocations(country: string) {
    const response = await api.call(
      schema.StandaloneTargetingLocations.list,
      { query: { country_code: country } },
      { headers: { Authorization: `Token ${PRO_TARGETING_AUTH_TOKEN}` } },
    )

    return response
  }

  return { getTargetingLocations, searchTargetingLocations }
}
